/**
 * @graphql agg
 */

import { useContext } from 'react';
import graphql from 'graphql-tag';
import { useLazyQuery } from '@apollo/react-hooks';

import { SPAViewContext } from '@confluence/spa-view-context';
import { useSessionData } from '@confluence/session-data';
import { getAGGClient } from '@confluence/graphql';

import { ConfluenceEdition } from '../Upgrade/__types__/waitForEditionChangeQuery';

const extractBillingAdminStatus = (billingData: any): boolean => {
	return (
		billingData?.tenantContexts?.[0]?.entitlementInfo?.entitlement?.subscription?.accountDetails
			?.invoiceGroup?.experienceCapabilities?.configurePayment?.isAvailableToUser ?? false
	);
};

export const useIsEligibleForUpflowV2ConfluenceStandardFGsExperiment = ({
	shouldShowUpflow,
}: {
	shouldShowUpflow: boolean;
}) => {
	const { cloudId, edition } = useSessionData();
	const { isSiteAdmin } = useContext(SPAViewContext);

	const isBasicEligible = Boolean(cloudId && edition === ConfluenceEdition.FREE && isSiteAdmin);

	const [
		executeEligibilityCheck,
		{ data: billingAdminData, loading: isBillingAdminQueryLoading, error: billingAdminQueryError },
		// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	] = useLazyQuery(
		graphql`
			query useUpflowV2ConfluenceStandardFGsExperimentBillingAdminQuery($cloudId: ID!) {
				tenantContexts(cloudIds: [$cloudId]) {
					entitlementInfo(hamsProductKey: "confluence.ondemand") {
						entitlement {
							subscription {
								accountDetails {
									invoiceGroup {
										experienceCapabilities {
											configurePayment {
												isAvailableToUser
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			client: getAGGClient(),
			variables: { cloudId },
		},
	);

	const isBillingAdmin = extractBillingAdminStatus(billingAdminData);

	return {
		isEligible: Boolean(isBasicEligible && isBillingAdmin),
		loading: isBillingAdminQueryLoading,
		error: billingAdminQueryError,
		executeEligibilityCheck: () => {
			if (shouldShowUpflow && isBasicEligible) {
				return executeEligibilityCheck();
			}
			return Promise.resolve();
		},
	};
};
